// One description of a season's settings, for every screen that edits them.
//
// The server writes a season row whole: any field the client leaves out is
// written as its default. Two host surfaces each sent their own half of the
// fields, so saving on one silently reset what the other had set. Saving in the
// host tools wiped a host's exact drop times, put drops per day back to 1, and
// reset lifelines and the week bonus. Saving in the setup wizard reset the
// answer timer, the check-in cadence and the wildcard days.
//
// So the shape of a save lives here, once, and both surfaces build from it.

import type { TimingStyle } from "./scheduling";
import type { CrewPurpose } from "./checkin-prompts";

export interface SeasonSettings {
  startsOn: string;
  endsOn: string;
  activeWeekdays: number[];
  dropsPerDay: number;
  timingStyle: TimingStyle;
  windowStart: string;
  windowEnd: string;
  exactTimes: string[];
  questionsPerDrop: number;
  playWindowMinutes: number;
  answerSeconds: number;
  lastCall: boolean;
  weekWinBonus: number;
  lifelines: number;
  checkInCadence: string;
  wildcardWeekdays: number[];
  wildcardTheme: string;
  purpose: CrewPurpose;
}

const today = () => new Date().toISOString().slice(0, 10);

export function defaultSeasonSettings(): SeasonSettings {
  return {
    startsOn: today(),
    endsOn: new Date(Date.now() + 13 * 86400000).toISOString().slice(0, 10),
    activeWeekdays: [1, 3, 5],
    dropsPerDay: 1,
    timingStyle: "surprise",
    windowStart: "09:00",
    windowEnd: "21:00",
    exactTimes: [],
    questionsPerDrop: 2,
    playWindowMinutes: 720,
    answerSeconds: 20,
    lastCall: true,
    weekWinBonus: 3,
    lifelines: 3,
    checkInCadence: "once",
    wildcardWeekdays: [],
    wildcardTheme: "random",
    purpose: "keeping_up",
  };
}

// Read a season row back into settings.
//
// play_window_minutes is the real column. drop_open_hours is the old one, kept
// in step and rounded UP, so reading it turned a 25 minute window into an hour
// and saving put that hour back. Minutes win; hours are only a fallback for a
// row written before the column existed.
export function seasonSettingsFromRow(row: any): SeasonSettings {
  const d = defaultSeasonSettings();
  if (!row) return d;
  return {
    startsOn: row.starts_on ?? d.startsOn,
    endsOn: row.ends_on ?? d.endsOn,
    activeWeekdays: row.active_weekdays ?? d.activeWeekdays,
    dropsPerDay: row.drops_per_day ?? d.dropsPerDay,
    timingStyle: row.timing_style === "exact" ? "exact" : "surprise",
    windowStart: row.drop_window_start_local ?? d.windowStart,
    windowEnd: row.drop_window_end_local ?? d.windowEnd,
    exactTimes: Array.isArray(row.exact_times_local) ? row.exact_times_local : [],
    questionsPerDrop: row.default_questions_per_drop ?? d.questionsPerDrop,
    playWindowMinutes: row.play_window_minutes
      ?? (row.drop_open_hours != null ? row.drop_open_hours * 60 : d.playWindowMinutes),
    answerSeconds: row.question_seconds ?? d.answerSeconds,
    lastCall: row.last_call_enabled !== false,
    weekWinBonus: row.week_win_bonus ?? d.weekWinBonus,
    lifelines: row.lifelines_per_season ?? d.lifelines,
    checkInCadence: row.check_in_cadence ?? d.checkInCadence,
    wildcardWeekdays: row.wildcard_weekdays ?? d.wildcardWeekdays,
    wildcardTheme: row.wildcard_theme ?? d.wildcardTheme,
    purpose: row.purpose === "getting_to_know" ? "getting_to_know" : "keeping_up",
  };
}

// Every field the server's configure reads. Sending the lot is what stops a
// save resetting whatever this screen happens not to show.
export function configurePayload(s: SeasonSettings): Record<string, unknown> {
  return {
    startsOn: s.startsOn,
    endsOn: s.endsOn,
    activeWeekdays: s.activeWeekdays,
    dropsPerDay: s.dropsPerDay,
    timingStyle: s.timingStyle,
    windowStart: s.windowStart,
    windowEnd: s.windowEnd,
    exactTimes: s.exactTimes,
    questionsPerDrop: s.questionsPerDrop,
    playWindowMinutes: s.playWindowMinutes,
    answerSeconds: s.answerSeconds,
    lastCall: s.lastCall,
    weekWinBonus: s.weekWinBonus,
    lifelines: s.lifelines,
    checkInCadence: s.checkInCadence,
    wildcardWeekdays: s.wildcardWeekdays,
    wildcardTheme: s.wildcardTheme,
    purpose: s.purpose,
  };
}

// What is wrong with these settings, in words a host can act on. An empty list
// means save it.
export function seasonSettingsProblems(s: SeasonSettings): string[] {
  const problems: string[] = [];
  if (!s.startsOn || !s.endsOn) problems.push("Pick a start and an end date.");
  else if (s.endsOn < s.startsOn) problems.push("The end date has to be on or after the start date.");
  if (s.activeWeekdays.length < 1) problems.push("Pick at least one drop day.");
  if (s.timingStyle === "exact") {
    if (s.exactTimes.length < 1) problems.push("Add at least one drop time, or switch back to surprise times.");
    if (s.exactTimes.length !== s.dropsPerDay) {
      problems.push(`You set ${s.dropsPerDay} drop${s.dropsPerDay === 1 ? "" : "s"} a day, so give ${s.dropsPerDay} time${s.dropsPerDay === 1 ? "" : "s"}.`);
    }
  } else if (s.windowEnd <= s.windowStart) {
    problems.push("The drop time range has to end after it starts.");
  }
  if (s.playWindowMinutes < 5) problems.push("Give people at least five minutes to play.");
  if (s.questionsPerDrop < 1) problems.push("A drop needs at least one question.");
  return problems;
}
